/**
 * TeamsSection - Grid of team cards for league page
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, MapPin } from "lucide-react";

export const TeamsSection = ({ teams, conferences, divisions, leagueColor, leagueSlug, leagueName }) => {
  const [conferenceFilter, setConferenceFilter] = useState("all");
  
  // Get conference name by id
  const getConferenceName = (confId) => {
    const conf = conferences.find(c => c.id === confId);
    return conf?.name || null;
  };
  
  // Get division abbreviations for a team
  const getTeamDivisions = (team) => {
    if (!team.division_entries || team.division_entries.length === 0) return [];
    return team.division_entries.map(entry => {
      // Try to get abbreviation from division lookup
      const divisionId = entry.division_id;
      if (divisionId) {
        const division = divisions.find(d => d.id === divisionId);
        if (division?.abbreviation) return division.abbreviation;
        if (division?.name) {
          const name = division.name;
          if (name.length <= 3) return name.toUpperCase();
          return name.substring(0, 3).toUpperCase();
        }
      }
      
      // Extract division from display_name (e.g., "Tyler Lions - Flag" -> "FLG")
      const displayName = entry.display_name || "";
      const parts = displayName.split(" - ");
      if (parts.length > 1) {
        const divName = parts[parts.length - 1].trim();
        if (divName.length <= 3) return divName.toUpperCase();
        // Common abbreviations
        const abbrevMap = {
          'flag': 'FLG',
          'freshman': 'FR',
          'jv': 'JV',
          'varsity': 'VAR',
          'junior': 'JR',
          'senior': 'SR',
          'youth': 'YTH'
        };
        return abbrevMap[divName.toLowerCase()] || divName.substring(0, 3).toUpperCase();
      }
      
      return null;
    }).filter(Boolean);
  };
  
  // Filter teams by conference
  const filteredTeams = teams.filter(team => {
    if (conferenceFilter === "all") return true;
    return team.conference_id === conferenceFilter;
  });
  
  // Sort teams alphabetically
  const sortedTeams = [...filteredTeams].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="font-heading text-xl uppercase text-white tracking-wide flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-500" />
          TEAMS ({filteredTeams.length})
        </h2>
        
        {/* Conference Filter */}
        {conferences.length > 0 && (
          <select
            value={conferenceFilter}
            onChange={(e) => setConferenceFilter(e.target.value)}
            className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
          >
            <option value="all">All Conferences</option>
            {conferences.map(conf => (
              <option key={conf.id} value={conf.id}>{conf.name}</option>
            ))}
          </select>
        )}
      </div>
      
      {sortedTeams.length === 0 ? (
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-12 text-center">
          <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">No teams found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedTeams.map(team => {
            const teamSlug = team.slug || team.name?.toLowerCase().replace(/\s+/g, '-');
            const conferenceName = getConferenceName(team.conference_id);
            const teamDivisions = getTeamDivisions(team);
            const teamLocation = [team.city, team.state].filter(Boolean).join(", ");
            
            return (
              <Link
                key={team.id}
                to={`/team/${teamSlug}`}
                state={{ 
                  from: "league", 
                  tab: "teams", 
                  leagueSlug: leagueSlug,
                  leagueName: leagueName 
                }}
                className="group block bg-zinc-900/50 rounded-xl border border-zinc-800 hover:border-zinc-600 transition-all overflow-hidden"
                data-testid={`team-card-${team.id}`}
              >
                {/* Team Card - Fixed height */}
                <div className="p-5 h-[180px] flex flex-col">
                  {/* Header: Logo + Name */}
                  <div className="flex items-start gap-4 mb-3">
                    {/* Logo */}
                    <div className="flex-shrink-0">
                      {team.logo_url ? (
                        <img 
                          src={team.logo_url} 
                          alt={team.name} 
                          className="w-16 h-16 object-contain"
                        />
                      ) : (
                        <div 
                          className="w-16 h-16 rounded-lg flex items-center justify-center text-xl font-bold text-white"
                          style={{ backgroundColor: team.color || leagueColor || "#333" }}
                        >
                          {team.abbreviation || team.name?.substring(0, 2)}
                        </div>
                      )}
                    </div>
                    
                    {/* Name & Mascot */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg uppercase text-white group-hover:text-orange-400 transition-colors truncate tracking-wide">
                        {team.name}
                      </h3>
                      {team.mascot && (
                        <p className="text-zinc-400 text-sm truncate">{team.mascot}</p>
                      )}
                      {teamLocation && (
                        <p className="text-zinc-500 text-xs flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {teamLocation}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Footer: Conference & Divisions */}
                  <div className="mt-auto space-y-2">
                    {conferenceName && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">Conference:</span>
                        <span 
                          className="text-xs px-2 py-0.5 rounded"
                          style={{ 
                            backgroundColor: `${leagueColor || '#f97316'}20`,
                            color: leagueColor || '#f97316'
                          }}
                        >
                          {conferenceName}
                        </span>
                      </div>
                    )}
                    {teamDivisions.length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-zinc-500">Divisions:</span>
                        {teamDivisions.map((div, idx) => (
                          <span 
                            key={idx}
                            className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded"
                          >
                            {div}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamsSection;
